import React from 'react'

// Scroll-reveal helper — fade/blur/scale up when entering viewport.
export function Reveal({ children, variant = 'up', delay = 0, style = {}, className, as = 'div' }) {
  const ref = React.useRef(null);
  const [shown, setShown] = React.useState(false);
  React.useEffect(() => {
    const el = ref.current; if (!el) return;
    const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) { setShown(true); return; }
    const io = new IntersectionObserver((es) => {
      es.forEach((e) => { if (e.isIntersecting) { setShown(true); io.disconnect(); } });
    }, { threshold: 0.18, rootMargin: '0px 0px -8% 0px' });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  const hidden = {
    up:    'translateY(36px)',
    blur:  'translateY(20px)',
    scale: 'scale(0.94)',
  }[variant];
  const Tag = as;
  return (
    <Tag ref={ref} className={className} style={{
      opacity: shown ? 1 : 0,
      transform: shown ? 'none' : hidden,
      filter: shown ? 'blur(0)' : (variant === 'blur' ? 'blur(10px)' : 'blur(0)'),
      transition: `opacity var(--dur-reveal) var(--ease-out) ${delay}s, transform var(--dur-reveal) var(--ease-out) ${delay}s, filter var(--dur-reveal) var(--ease-out) ${delay}s`,
      ...style,
    }}>
      {children}
    </Tag>
  );
}

// Section wrapper with consistent rhythm + container.
export function Section({ children, dark = false, id, style = {}, contentStyle = {}, label }) {
  return (
    <section id={id} data-screen-label={label} style={{
      padding: 'var(--section-y) 0',
      background: dark ? 'var(--surface-inverse)' : 'transparent',
      color: dark ? 'var(--text-on-dark)' : 'var(--text-body)',
      position: 'relative', overflow: 'hidden', ...style,
    }}>
      <div style={{ maxWidth: 'var(--container-xl)', margin: '0 auto', padding: '0 var(--gutter)', position: 'relative', zIndex: 1, ...contentStyle }}>
        {children}
      </div>
    </section>
  );
}

export function Eyebrow({ children, dark }) {
  return <span style={{
    display: 'inline-block', fontFamily: 'var(--font-ui)', fontSize: 'var(--fs-eyebrow)',
    fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase',
    color: dark ? 'var(--energy-bright)' : 'var(--brand-green)', marginBottom: 16,
  }}>{children}</span>;
}

export function SectionHead({ eyebrow, title, sub, dark, align = 'left', max = 720 }) {
  return (
    <Reveal style={{ maxWidth: max, margin: align === 'center' ? '0 auto' : 0, textAlign: align }}>
      {eyebrow && <Eyebrow dark={dark}>{eyebrow}</Eyebrow>}
      <h2 style={{
        fontFamily: 'var(--font-heading)', fontWeight: 700, fontSize: 'var(--fs-h2)',
        letterSpacing: '-0.02em', lineHeight: 1.1, margin: 0,
        color: dark ? '#fff' : 'var(--text-strong)',
      }}>{title}</h2>
      {sub && <p style={{
        marginTop: 16, fontSize: 'var(--fs-lead)', lineHeight: 1.55,
        color: dark ? 'var(--text-on-dark-muted)' : 'var(--text-muted)',
      }}>{sub}</p>}
    </Reveal>
  );
}
